/**
 * Package manifest generator for data packages
 * 
 * Generates manifest.json files that describe the contents of data packages
 */

import { ProcessedLibraryItem, LibraryItemsByBlueprint } from '../integrations/libraryProcessorShared';

/**
 * Generate a simple UUID v4
 */
function generateUuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Package manifest structure
 */
export interface PackageManifest {
  package_id: string;
  generated_at: string;
  producer: string;
  game_id: string;
  game_name: string;
  schema_version: string;
  platform: 'cpp' | 'roblox';
  objects: PackageObject[];
}

/**
 * Object entry in package manifest
 */
export interface PackageObject {
  object_id: string;
  name: string;
  blueprint_id: string;
  blueprint_name: string;
  blueprint_version: string;
  data_file: string;
  base_file: string;
  user_file: string;
}

/**
 * Generate package manifest
 */
export function generateManifest(
  blueprintGroups: LibraryItemsByBlueprint[],
  gameId: string,
  gameName: string,
  platform: 'cpp' | 'roblox'
): PackageManifest {
  const objects: PackageObject[] = [];
  
  const fileExtension = platform === 'cpp' ? '.h' : '.luau';
  
  blueprintGroups.forEach(group => {
    group.items.forEach(item => {
      const baseFileName = sanitizeBlueprintName(group.blueprintName) + 'Base' + fileExtension;
      const userFileName = sanitizeItemName(item.name) + fileExtension;
      const dataFileName = sanitizeItemName(item.name) + '.json';
      
      objects.push({
        object_id: item.id,
        name: item.name,
        blueprint_id: group.blueprintId,
        blueprint_name: group.blueprintName,
        blueprint_version: group.blueprintVersion,
        data_file: `data/${dataFileName}`,
        base_file: `generated/${baseFileName}`,
        user_file: `user/${userFileName}`
      });
    });
  });
  
  return {
    package_id: generateUuid(),
    generated_at: new Date().toISOString(),
    producer: 'LocaNest-1.0.0',
    game_id: gameId,
    game_name: gameName,
    schema_version: '1.0.0',
    platform,
    objects
  };
}

/**
 * Sanitize blueprint name for filename
 */
function sanitizeBlueprintName(name: string): string {
  return name.replace(/[^a-zA-Z0-9]/g, '');
}

/**
 * Sanitize item name for filename
 */
function sanitizeItemName(name: string): string {
  return name.replace(/[^a-zA-Z0-9-_]/g, '_').replace(/_{2,}/g, '_');
}

/**
 * Generate manifest as JSON string
 */
export function generateManifestJson(
  blueprintGroups: LibraryItemsByBlueprint[],
  gameId: string,
  gameName: string,
  platform: 'cpp' | 'roblox'
): string {
  const manifest = generateManifest(blueprintGroups, gameId, gameName, platform);
  return JSON.stringify(manifest, null, 2);
}

